import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const num = (v) => Number(String(v).replace(/[^0-9.]/g, "")) || 0;

function compute({ arv, repairs, seller, rent }) {
  const wholesale = 20000;
  const closing = 0.07 * arv;
  const holding = 0.05 * arv;
  const buyer = seller + wholesale;
  const allIn = buyer + repairs + closing + holding;
  const profit = arv - allIn;
  return {
    wholesale, closing, holding, buyer, allIn, profit,
    profitPct: arv > 0 ? (profit / arv) * 100 : 0,
    rentPct: arv > 0 ? ((rent * 12) / arv) * 100 : 0,
  };
}

export default function ProfitCalculator() {
  const [params] = useSearchParams();
  const leadId = params.get("lead_id") || "";
  const [arv, setArv] = useState("315000");
  const [repairs, setRepairs] = useState("15000");
  const [seller, setSeller] = useState("160000");
  const [rent, setRent] = useState("0");
  const [saving, setSaving] = useState(false);

  const r = useMemo(
    () => compute({ arv: num(arv), repairs: num(repairs), seller: num(seller), rent: num(rent) }),
    [arv, repairs, seller, rent],
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/profit-calculator", {
        lead_id: leadId || undefined,
        arv: num(arv), repairs: num(repairs),
        contract_price_seller: num(seller), monthly_rent: num(rent),
      });
      toast.success(leadId ? "Saved to the lead's record" : "Calculator saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const Row = ({ label, value, strong, tone }) => (
    <div className={`flex items-center justify-between py-2 border-b border-[var(--app-line)] last:border-0 ${strong ? "text-base font-semibold" : "text-sm"}`}>
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span className={tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-red-600" : ""}>{value}</span>
    </div>
  );

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto" data-testid="profit-calculator-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center">
          <Calculator size={18} strokeWidth={2.2} />
        </div>
        <div>
          <h1 className="font-display text-2xl md:text-3xl leading-tight">Profit Calculator</h1>
          <p className="text-sm text-muted-foreground">
            Wholesale math — $20k fee, 7% closing, 5% holding{leadId ? " · saves to this lead" : ""}.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-flat p-5 space-y-4" data-testid="pc-inputs">
          <div>
            <Label className="text-xs">ARV (After Repair Value)</Label>
            <Input value={arv} onChange={(e) => setArv(e.target.value)} className="mt-1" data-testid="pc-arv" />
          </div>
          <div>
            <Label className="text-xs">Repairs ($)</Label>
            <Input value={repairs} onChange={(e) => setRepairs(e.target.value)} className="mt-1" data-testid="pc-repairs" />
          </div>
          <div>
            <Label className="text-xs">Contract Price to Seller</Label>
            <Input value={seller} onChange={(e) => setSeller(e.target.value)} className="mt-1" data-testid="pc-seller" />
          </div>
          <div>
            <Label className="text-xs">Monthly Rent (optional)</Label>
            <Input value={rent} onChange={(e) => setRent(e.target.value)} className="mt-1" data-testid="pc-rent" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="pc-save"
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : <Save size={15} className="mr-1" />}
            Save Calculator
          </Button>
        </div>

        <div className="card-flat p-5" data-testid="pc-results">
          <Row label="Contract Price to Buyer" value={money(r.buyer)} />
          <Row label="Wholesale Fee (fixed)" value={money(r.wholesale)} />
          <Row label="Closing Cost (7% ARV)" value={money(r.closing)} />
          <Row label="Holding Cost (5% ARV)" value={money(r.holding)} />
          <Row label="Repairs" value={money(num(repairs))} />
          <Row label="All-In Number" value={money(r.allIn)} strong />
          <Row label="Potential Profit" value={money(r.profit)} strong tone={r.profit >= 0 ? "pos" : "neg"} />
          <Row label="Profit %" value={`${r.profitPct.toFixed(1)}%`} tone={r.profit >= 0 ? "pos" : "neg"} />
          <Row label="Rent %" value={`${r.rentPct.toFixed(2)}%`} />
        </div>
      </div>
    </div>
  );
}
